/**
 * Renders a video primitive node.
 *
 * The media source can be a static URL (node.src) or a protected API URL.
 */

import { Box } from "@mui/material";
import React from "react";
import dico from "../../../../util/src/language/dico";
import type { InjectedData, PrimitiveNode } from "../layout-types";
import { resolveStyles } from "../layout-types";
import { useProtectedMediaSrc } from "../../layout-compiler/useProtectedMediaSrc";

interface LayoutVideoProps {
  node: PrimitiveNode;
  data: InjectedData;
}

const toCssLength = (value: unknown): string | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return `${Math.max(0, value)}px`;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    return value.trim();
  }
  return undefined;
};

const LayoutVideo: React.FC<LayoutVideoProps> = ({ node, data }) => {
  const resolved = resolveStyles(node.style, data);
  const src = useProtectedMediaSrc(node.src);
  const isHidden = resolved.visibility === false;
  const videoLabel = dico().licensedPortal.layoutEditor.toolbar.video;

  if (isHidden) return null;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        opacity: resolved.opacity as number | undefined,
        borderRadius: toCssLength(resolved.borderRadius),
        borderTopLeftRadius: toCssLength(resolved.borderTopLeftRadius),
        borderTopRightRadius: toCssLength(resolved.borderTopRightRadius),
        borderBottomRightRadius: toCssLength(resolved.borderBottomRightRadius),
        borderBottomLeftRadius: toCssLength(resolved.borderBottomLeftRadius),
        border: resolved.border as string | undefined,
        borderTop: resolved.borderTop as string | undefined,
        borderRight: resolved.borderRight as string | undefined,
        borderBottom: resolved.borderBottom as string | undefined,
        borderLeft: resolved.borderLeft as string | undefined,
        boxSizing: "border-box",
      }}
    >
      {src ? (
        <video
          src={src}
          muted={node.muted ?? false}
          loop={node.loop ?? true}
          autoPlay
          playsInline
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
          }}
        />
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            backgroundColor: "#f0f0f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#999",
            fontSize: 12,
          }}
        >
          {node.label ?? videoLabel}
        </Box>
      )}
    </Box>
  );
};

export default LayoutVideo;
